'use client'

import { Label } from '@/components/ui/label'
import { LIGHT_FIELD_CLASS, LIGHT_HELP_CLASS } from '@/components/santa-video/light-field'
import { OptionalTag } from '@/components/santa-video/optional-tag'
import { OrderListField } from '@/components/santa-video/order-list-field'
import { OrderTextField } from '@/components/santa-video/order-text-field'
import type { OrderFormValues } from '@/components/santa-video/order-form-body'
import { EMAIL_FIELD_ID } from '@/components/santa-video/wizard/wizard-steps'
import type { BookOrderFormCopy, OrderFormCopy } from '@/lib/santa-video/order-page-copy'
import { BOOK_MAX_ENTRIES, BOOK_MIN_PERSONAL_DETAILS, minInterestsFor } from '@/server/domain/santa-video/book-order-form'
import { AGE_RANGE, MAX_ENTRIES, MAX_LENGTH, MIN_PERSONAL_DETAILS } from '@/server/domain/santa-video/order-form'

/** Los campos de cada paso del wizard. Los topes salen del dominio, en un solo sitio. */

export type FieldSetter = <K extends keyof OrderFormValues>(key: K) => (value: OrderFormValues[K]) => void
/** Genérico en la copy: el libro la amplía (`BookOrderFormCopy`) y sus campos evolucionan con los de la carta. */
export interface StepFieldsProps<C extends OrderFormCopy = OrderFormCopy> { copy: C; values: OrderFormValues; set: FieldSetter }

export function ChildFields({ copy, values, set }: StepFieldsProps) {
  return (
    <>
      <OrderTextField id="video-order-name" label={copy.nameLabel} maxLength={MAX_LENGTH.name} onChange={(e) => set('name')(e.target.value)} placeholder={copy.namePlaceholder} required value={values.name} />
      <div className="grid gap-4 sm:grid-cols-2">
        <OrderTextField id="video-order-age" inputMode="numeric" optionalTag={copy.optionalTag} label={copy.ageLabel} max={AGE_RANGE.max} min={AGE_RANGE.min} onChange={(e) => set('age')(e.target.value)} type="number" value={values.age} />
        <OrderTextField id="video-order-city" label={copy.cityLabel} optionalTag={copy.optionalTag} maxLength={MAX_LENGTH.city} onChange={(e) => set('city')(e.target.value)} value={values.city} />
      </div>
    </>
  )
}

export function KnowledgeFields({ copy, values, set }: StepFieldsProps) {
  const common = { addLabel: copy.addAnother, maxLength: MAX_LENGTH.listEntry, removeLabel: copy.remove }
  return (
    <>
      <OrderListField {...common} help={copy.detailsHelp} id="details" label={copy.detailsLabel} maxEntries={MAX_ENTRIES.personalDetails} minEntries={MIN_PERSONAL_DETAILS} onChange={set('personalDetails')} placeholder={copy.detailsPlaceholder} values={values.personalDetails} />
      <OrderListField {...common} id="interests" optionalTag={copy.optionalTag} label={copy.interestsLabel} maxEntries={MAX_ENTRIES.interests} onChange={set('interests')} values={values.interests} />
      <OrderListField {...common} help={copy.authorizedGiftsHelp} id="authorized" optionalTag={copy.optionalTag} label={copy.authorizedGiftsLabel} maxEntries={MAX_ENTRIES.authorizedGifts} onChange={set('authorizedGifts')} values={values.authorizedGifts} />
      <OrderListField {...common} help={copy.requestedGiftsHelp} id="requested" optionalTag={copy.optionalTag} label={copy.requestedGiftsLabel} maxEntries={MAX_ENTRIES.requestedGifts} onChange={set('requestedGifts')} values={values.requestedGifts} />
    </>
  )
}

/**
 * El paso 2 del Gran Libro: lo que los elfos anotaron (un hecho basta), lo que
 * le gusta y un reto opcional, sin listas de regalos —el niño aún no ha
 * escrito su carta—. Los mínimos salen del dominio: sin reto no se puede
 * quitar el segundo interés; quien lo comprueba al avanzar es `validateStep`.
 */
export function BookKnowledgeFields({ copy, values, set }: StepFieldsProps<BookOrderFormCopy>) {
  const common = { addLabel: copy.addAnother, maxLength: MAX_LENGTH.listEntry, removeLabel: copy.remove }
  return (
    <>
      <OrderListField {...common} help={copy.detailsHelp} id="details" label={copy.detailsLabel} maxEntries={BOOK_MAX_ENTRIES.personalDetails} minEntries={BOOK_MIN_PERSONAL_DETAILS} onChange={set('personalDetails')} placeholder={copy.detailsPlaceholder} values={values.personalDetails} />
      <OrderListField {...common} help={copy.interestsHelp} id="interests" label={copy.interestsLabel} maxEntries={BOOK_MAX_ENTRIES.interests} minEntries={minInterestsFor(values.challenge.trim() || undefined)} onChange={set('interests')} values={values.interests} />
      <OrderTextField help={copy.challenge.help} id="video-order-challenge" label={copy.challenge.label} maxLength={MAX_LENGTH.listEntry} onChange={(e) => set('challenge')(e.target.value)} optionalTag={copy.optionalTag} placeholder={copy.challenge.placeholder} value={values.challenge} />
    </>
  )
}

interface DeliveryFieldsProps extends StepFieldsProps {
  consent: boolean
  onConsent: (value: boolean) => void
  privacyHref: string
  priceLabel: string
}

export function DeliveryFields({ copy, values, set, consent, onConsent, privacyHref, priceLabel }: DeliveryFieldsProps) {
  return (
    <>
      <div className="flex flex-col gap-2">
        <Label className="block text-stone-800" htmlFor="video-order-message">{copy.parentMessageLabel}<OptionalTag tag={copy.optionalTag} /></Label>
        {copy.parentMessageHelp && <p className={LIGHT_HELP_CLASS}>{copy.parentMessageHelp}</p>}
        <textarea className={`${LIGHT_FIELD_CLASS} h-auto min-h-24 rounded-md border px-3 py-2`} id="video-order-message" maxLength={MAX_LENGTH.parentMessage} onChange={(e) => set('parentMessage')(e.target.value)} value={values.parentMessage} />
      </div>
      <OrderTextField autoComplete="email" help={copy.emailHelp} id={EMAIL_FIELD_ID} label={copy.emailLabel} onChange={(e) => set('email')(e.target.value)} required type="email" value={values.email} />
      <label className="flex items-start gap-2 text-sm text-stone-700">
        <input checked={consent} className="mt-1 accent-red-700" onChange={(e) => onConsent(e.target.checked)} required type="checkbox" />
        <span>{copy.consent} <a className="underline" href={privacyHref}>{copy.privacyLinkLabel}</a>.</span>
      </label>
      <OrderSummary childName={values.name.trim()} copy={copy} priceLabel={priceLabel} />
    </>
  )
}

function OrderSummary({ copy, childName, priceLabel }: { copy: OrderFormCopy; childName: string; priceLabel: string }) {
  return (
    <dl className="rounded-2xl bg-stone-100 p-4 text-stone-800">
      <dt className="text-xs font-semibold uppercase tracking-wide text-stone-500">{copy.summaryTitle}</dt>
      <dd className="mt-1">{copy.summaryItem} <strong>{childName}</strong></dd>
      <dd className="mt-2 flex items-baseline justify-between border-t border-stone-200 pt-2">
        <span>{copy.totalLabel}</span>
        <strong className="text-2xl">{priceLabel}</strong>
      </dd>
    </dl>
  )
}
